import { INVALID_MOVE } from 'boardgame.io/core';
import { debug } from '../../utils/debug';
import type { Ctx } from 'boardgame.io';
import type { AoSGameState, BuildSpecialLinkArgs, CompletedLink } from '../../types';
import { validateBuildSpecialLink } from '../validators/buildSpecialLinkValidator';
import { updateBuildCostInfo } from './passBuildTrack';

/**
 * 特殊リンク（フェリー/隣接）敷設 Move
 *
 * フェリーリンクや隣接リンクを敷設する。
 * タイルは配置せず、コストのみ支払い、所有権を記録する。
 *
 * @param G - ゲーム状態
 * @param ctx - Boardgame.io コンテキスト
 * @param args - 敷設引数
 * @returns void or INVALID_MOVE
 */
export function buildSpecialLink(
  { G, ctx }: { G: AoSGameState; ctx: Ctx },
  args: BuildSpecialLinkArgs
): void | typeof INVALID_MOVE {
  // 1. バリデーション
  const validation = validateBuildSpecialLink(G, ctx, args);
  if (!validation.success) {
    console.error(`[buildSpecialLink] Validation failed: ${validation.error} - ${validation.message}`);
    return INVALID_MOVE;
  }

  const player = G.players[ctx.currentPlayer];

  // 2. リンクを取得
  let link;
  if (args.linkType === 'ferry') {
    link = G.ferryLinks.find(l => l.id === args.linkId);
  } else {
    link = G.adjacentLinks.find(l => l.id === args.linkId);
  }

  if (!link) {
    console.error(`[buildSpecialLink] Link not found: ${args.linkId}`);
    return INVALID_MOVE;
  }

  // 3. コスト取得（支払いは passBuildTrack で一括払い）
  const cost = link.cost;
  // コスト支払いは passBuildTrack で一括払い（エンジニアボーナス対応）
  // player.cash -= cost;

  // 4. 所有権記録
  link.owner = ctx.currentPlayer;

  // 5. タイル配置数を加算（特殊リンクも1タイル分としてカウント）
  player.tilesPlacedThisTurn += 1;

  // 6. CompletedLink に追加（配置即リンク完成）
  const fromHex = G.map.find(h => h.coordinate.q === link.from.q && h.coordinate.r === link.from.r);
  const toHex = G.map.find(h => h.coordinate.q === link.to.q && h.coordinate.r === link.to.r);

  // 都市または町のIDを取得（新都市化されている場合は newCity の id を使用）
  const getLocationId = (hex: typeof fromHex): string => {
    if (!hex) return '';
    if (hex.city) return hex.city.id;
    if (hex.town) {
      return hex.town.newCity ? hex.town.newCity.id : hex.town.id;
    }
    return '';
  };

  // ゲームターン: actualTurn（Barbados/Detroit等）を優先、なければ ctx.turn
  const gameActualTurn = (G as any).actualTurn ?? ctx.turn ?? 0;

  const completedLink: CompletedLink = {
    id: `${args.linkType}-link-${link.id}`,
    startId: getLocationId(fromHex),
    endId: getLocationId(toHex),
    path: [link.from, link.to],
    owner: ctx.currentPlayer,
    linkCount: 1,  // 特殊リンクは1リンク
    completedTurn: gameActualTurn,
  };
  G.completedLinks.push(completedLink);

  // 7. 履歴に記録（Cancel対応、コスト一括払い対応）
  if (!player.buildTrackHistory) {
    player.buildTrackHistory = [];
  }
  player.buildTrackHistory.push({
    hexCoordinate: link.from,
    tileType: 'STRAIGHT' as any,  // 特殊リンクにはタイル種別がないのでダミー
    rotation: 0,
    placementType: 'place',
    cost,
    previousTile: null,
    previousTownState: null,
    isNewCityTile: false,
    isSpecialLink: true,
    specialLinkId: args.linkId,
    specialLinkType: args.linkType,
  });

  debug.log(`[buildSpecialLink] Player ${ctx.currentPlayer} built ${args.linkType} link ${args.linkId}, cost: $${cost}, completedLink: ${completedLink.startId} -> ${completedLink.endId}`);

  // 8. 建設コスト情報を更新（UI表示用）
  updateBuildCostInfo(G, ctx.currentPlayer);
}
